/**
 * Utility functions for interacting with browser's localStorage
 */

/**
 * Save data to localStorage
 * @param key - The key to store the data under
 * @param value - The data to store (will be JSON stringified)
 */
export const saveToLocalStorage = <T>(key: string, value: T): void => {
  try {
    const serializedValue = JSON.stringify(value);
    localStorage.setItem(key, serializedValue);
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

/**
 * Get data from localStorage
 * @param key - The key to retrieve data from
 * @param defaultValue - Default value to return if key doesn't exist
 * @returns The parsed data or defaultValue if not found
 */
export const getFromLocalStorage = <T>(key: string, defaultValue: T): T => {
  let serializedValue: string | null;
  try {
    serializedValue = localStorage.getItem(key);
  } catch (error) {
    // localStorage access can fail in private browsing or restrictive
    // security contexts.
    console.error('Error getting from localStorage:', error);
    return defaultValue;
  }

  if (serializedValue === null) {
    return defaultValue;
  }

  try {
    return JSON.parse(serializedValue) as T;
  } catch {
    // Tolerate raw (non-JSON-encoded) string values that may have been
    // written by other apps sharing this localStorage origin or by older
    // versions of this app. When the caller expects a string, returning
    // the raw value keeps them working; otherwise fall back to the default.
    if (typeof defaultValue === 'string') {
      return serializedValue as unknown as T;
    }
    return defaultValue;
  }
};

/**
 * Remove data from localStorage
 * @param key - The key to remove
 */
export const removeFromLocalStorage = (key: string): void => {
  try {
    localStorage.setItem(key, '');
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error removing from localStorage:', error);
  }
};

/**
 * Clear all data from localStorage
 */
export const clearLocalStorage = (): void => {
  try {
    localStorage.clear();
  } catch (error) {
    console.error('Error clearing localStorage:', error);
  }
};

/**
 * Check if a key exists in localStorage
 * @param key - The key to check
 * @returns True if the key exists, false otherwise
 */
export const hasLocalStorageItem = (key: string): boolean => {
  try {
    return localStorage.getItem(key) !== null;
  } catch (error) {
    console.error('Error checking localStorage item:', error);
    return false;
  }
};
